import * as React from 'react';
import { flushSync } from 'react-dom';
import {
  Bot,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Minus,
  Eye,
  Copy,
  AlertTriangle,
  Scale,
  Shuffle,
  Scissors,
  Lightbulb,
  ListOrdered,
} from 'lucide-react';
import { ChatComposer, type AgentComposerMode, type ChatComposerSubmitPayload } from './ChatComposer';
import { PlaybookPickerModal, type LinkedPlaybookSelection } from './PlaybookPickerModal';
import { filesToAttachmentPayloads } from '../../utils/fileAttachments';
import { AskMarkdown } from './AskMarkdown';
import apiService from '../../services/api';
import { DocumentReader } from '../../word/api/DocumentReader';
import { ProofreadWordAdapter } from '../../features/proofread/ProofreadWordAdapter';
import { executeAgentToolCalls } from '../../features/agent/AgentWordTools';
import { log, logError } from '../../utils/logging';
import {
  getReviewPartyContextInstruction,
  hasReviewPartyContextComplete,
} from '../../utils/reviewPartyContext';
import { detectTextDirection } from '../../utils/textUtils';
import type { AgentIssue, AgentIssueCategory, AgentScanStatus } from '../../features/agent/types';
import { CATEGORY_LABELS, SEVERITY_ORDER } from '../../features/agent/types';
import './AskView.css';
import './AgentView.css';

const CATEGORY_ORDER: AgentIssueCategory[] = [
  'risk',
  'legal_improvement',
  'consistency',
  'simplification',
  'clarity',
];

const CATEGORY_ICONS: Record<AgentIssueCategory, React.ReactNode> = {
  risk: <AlertTriangle size={14} />,
  legal_improvement: <Scale size={14} />,
  consistency: <Shuffle size={14} />,
  simplification: <Scissors size={14} />,
  clarity: <Lightbulb size={14} />,
};

let _idCounter = 0;
function nextId(): string {
  return `ag-${++_idCounter}-${Date.now()}`;
}

type FilterMode = 'open' | 'all';

/** One completed agent turn — kept in chronological order */
type AgentThreadRound = {
  id: string;
  userMessage: string;
  plan: string[];
  planStepCompleted: number;
  activityLog: string[];
  roundSummary: string;
  issues: AgentIssue[];
};

const MAX_ORCHESTRATION_ITERATIONS = 8;

/** Serialized prior thread for the engine to revise/extend/drop issues on follow-up */
function buildPreviousRoundsContext(rs: AgentThreadRound[]) {
  return rs.map((r) => ({
    user_message: r.userMessage,
    round_summary: r.roundSummary,
    issues: r.issues.map((i) => ({
      paragraphIndex: i.paragraphIndex,
      originalText: i.originalText,
      suggestedText: i.suggestedText,
      explanation: i.explanation,
      category: i.category,
      severity: i.severity,
      groupId: i.groupId,
      status: i.status,
    })),
  }));
}

function defaultCatExpanded(): Set<AgentIssueCategory> {
  return new Set(CATEGORY_ORDER);
}

interface AgentViewProps {
  /** Parent project ID — used to list playbooks. When absent the playbook icon is disabled. */
  parentProjectId?: string;
  /** Drives which backend endpoint is preferred for listing playbooks. */
  workflowHint?: 'construction_loan' | 'real_estate';
  onRequireReview?: () => void;
}

export const AgentView: React.FC<AgentViewProps> = ({
  parentProjectId,
  workflowHint,
  onRequireReview,
}) => {
  const [goal, setGoal] = React.useState('');
  const [primaryGoal, setPrimaryGoal] = React.useState<string | null>(null);
  const [rounds, setRounds] = React.useState<AgentThreadRound[]>([]);
  const [scanStatus, setScanStatus] = React.useState<AgentScanStatus>('idle');
  const [scanError, setScanError] = React.useState<string | null>(null);

  /** In-flight request: narration + plan update live */
  const [liveActivityLog, setLiveActivityLog] = React.useState<string[]>([]);
  const [livePlan, setLivePlan] = React.useState<string[]>([]);
  const [livePlanStepCompleted, setLivePlanStepCompleted] = React.useState(0);
  const [pendingUserMessage, setPendingUserMessage] = React.useState<string | null>(null);

  const [filterModeByRound, setFilterModeByRound] = React.useState<Record<string, FilterMode>>({});
  const [expandedCategoriesByRound, setExpandedCategoriesByRound] = React.useState<
    Record<string, Set<AgentIssueCategory>>
  >({});
  const [replaceAllResult, setReplaceAllResult] = React.useState<{
    roundId: string;
    succeeded: number;
    failed: number;
  } | null>(null);

  /** true = suggestion list hidden (only for non-latest rounds; latest is always expanded) */
  const [issuesListCollapsedByRoundId, setIssuesListCollapsedByRoundId] = React.useState<
    Record<string, boolean>
  >({});

  /** Rounds marked stale as soon as the user sends a follow-up (until a newer round exists) */
  const [staleRoundIds, setStaleRoundIds] = React.useState<Set<string>>(() => new Set());

  const [typedNarration, setTypedNarration] = React.useState('');
  /** Suggestions = review + issues list. Draft = generate/insert structured text in Word. */
  const [agentMode, setAgentMode] = React.useState<AgentComposerMode>('suggestions');

  /** Playbook picker state */
  const [playbookPickerOpen, setPlaybookPickerOpen] = React.useState(false);
  const [linkedPlaybook, setLinkedPlaybook] = React.useState<LinkedPlaybookSelection | null>(null);

  const activityEndRef = React.useRef<HTMLDivElement>(null);
  /** Thread scroll container — follow-up runs append the pending block below prior rounds; keep it in view */
  const transcriptScrollRef = React.useRef<HTMLDivElement>(null);

  const hasSubmittedOnce = scanStatus !== 'idle';
  const lastNarrationKey =
    liveActivityLog.length > 0 ? liveActivityLog[liveActivityLog.length - 1] : '';

  React.useEffect(() => {
    if (!lastNarrationKey || scanStatus !== 'scanning') {
      setTypedNarration('');
      return;
    }
    let i = 0;
    setTypedNarration('');
    const id = window.setInterval(() => {
      i += 2;
      setTypedNarration(lastNarrationKey.slice(0, i));
      if (i >= lastNarrationKey.length) {
        window.clearInterval(id);
      }
    }, 12);
    return () => window.clearInterval(id);
  }, [lastNarrationKey, scanStatus]);

  React.useLayoutEffect(() => {
    if (scanStatus !== 'scanning' || !pendingUserMessage) return;
    const el = transcriptScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [
    scanStatus,
    pendingUserMessage,
    liveActivityLog,
    typedNarration,
    livePlan,
    livePlanStepCompleted,
  ]);

  function getFilterMode(roundId: string): FilterMode {
    return filterModeByRound[roundId] ?? 'open';
  }

  function setFilterMode(roundId: string, mode: FilterMode) {
    setFilterModeByRound((prev) => ({ ...prev, [roundId]: mode }));
  }

  function getExpandedSet(roundId: string): Set<AgentIssueCategory> {
    return expandedCategoriesByRound[roundId] ?? defaultCatExpanded();
  }

  function toggleCategoryRound(roundId: string, cat: AgentIssueCategory) {
    setExpandedCategoriesByRound((prev) => {
      const cur = new Set(prev[roundId] ?? defaultCatExpanded());
      if (cur.has(cat)) cur.delete(cat);
      else cur.add(cat);
      return { ...prev, [roundId]: cur };
    });
  }

  function togglePrevIssuesList(roundId: string) {
    setIssuesListCollapsedByRoundId((prev) => ({
      ...prev,
      [roundId]: !(prev[roundId] ?? true),
    }));
  }

  async function runScan(payload?: ChatComposerSubmitPayload) {
    const trimmedGoal = (payload?.text ?? goal).trim();
    const attachmentFiles = payload?.attachments ?? [];
    if (!trimmedGoal && attachmentFiles.length === 0) {
      return;
    }
    if (scanStatus === 'scanning') return;

    if (!hasReviewPartyContextComplete()) {
      onRequireReview?.();
      return;
    }

    const newMessage =
      trimmedGoal ||
      (attachmentFiles.length
        ? `Attached: ${attachmentFiles.map((f) => f.name).join(', ')}`
        : '');
    const goalForApi = primaryGoal ?? newMessage;
    const followUpForApi = primaryGoal ? newMessage : undefined;
    const priorRoundsForApi =
      rounds.length > 0 ? buildPreviousRoundsContext(rounds) : undefined;

    if (rounds.length > 0) {
      setStaleRoundIds(new Set(rounds.map((r) => r.id)));
      setIssuesListCollapsedByRoundId((prev) => {
        const next = { ...prev };
        rounds.forEach((r) => {
          next[r.id] = true;
        });
        return next;
      });
    }

    if (!primaryGoal) {
      setPrimaryGoal(newMessage);
    }

    const sentGoal = newMessage;
    setPendingUserMessage(newMessage);
    setGoal('');
    setScanStatus('scanning');
    setScanError(null);
    setReplaceAllResult(null);
    setLiveActivityLog([]);
    setLivePlan([]);
    setLivePlanStepCompleted(0);

    log('[AgentFlow] runScan:start', {
      goal: goalForApi,
      followUp: followUpForApi,
      priorRoundsCount: priorRoundsForApi?.length ?? 0,
      agentMode,
    });

    const startedAt = Date.now();

    try {
      let attachmentPayloads:
        | Awaited<ReturnType<typeof filesToAttachmentPayloads>>
        | undefined;
      if (attachmentFiles.length > 0) {
        attachmentPayloads = await filesToAttachmentPayloads(attachmentFiles);
      }

      // Whole-project playbook link: all records (author + paragraph_text), built in modal
      let playbookContext: string | undefined;
      if (linkedPlaybook?.contextBody?.trim()) {
        playbookContext = linkedPlaybook.contextBody.trim().slice(0, 200_000);
      }

      let iterationToolResults: Array<{ id: string; name: string; result?: string; error?: string }> | undefined;
      const narrationsAccum: string[] = [];
      let frozenPlan: string[] = [];

      for (let iter = 0; iter < MAX_ORCHESTRATION_ITERATIONS; iter++) {
        const { paragraphs, fullText } = await DocumentReader.getFullDocumentContext();
        log('[AgentFlow] orchestrate:context', {
          iter,
          paragraphCount: paragraphs.length,
          fullTextLength: fullText.length,
        });

        const step = await apiService.agentOrchestrateStep({
          goal: goalForApi,
          documentText: fullText,
          paragraphs,
          reviewPartyContext: getReviewPartyContextInstruction(),
          followUp: followUpForApi,
          previousRoundsContext: iter === 0 ? priorRoundsForApi : undefined,
          toolResults: iterationToolResults,
          iterationIndex: iter,
          frozenPlan: iter > 0 && frozenPlan.length > 0 ? frozenPlan : undefined,
          agentMode,
          attachments: iter === 0 ? attachmentPayloads : undefined,
          playbookContext: iter === 0 ? playbookContext : undefined,
        });

        if (step.plan?.length && frozenPlan.length === 0) {
          frozenPlan = [...step.plan];
        }

        const stablePlan = frozenPlan.length > 0 ? frozenPlan : step.plan;
        let stablePlanStep = step.planStepCompleted;
        if (stablePlan.length > 0) {
          stablePlanStep = Math.min(
            Math.max(0, stablePlanStep),
            Math.max(0, stablePlan.length - 1)
          );
        }
        // Completed round: entire plan is done (engine enforces; keep UI correct if model drifts).
        if (step.phase === 'final' && stablePlan.length > 0) {
          stablePlanStep = stablePlan.length - 1;
        }

        if (step.narration?.trim()) {
          const line = step.narration.trim();
          narrationsAccum.push(line);
        }
        flushSync(() => {
          if (step.narration?.trim()) {
            const line = step.narration.trim();
            setLiveActivityLog((prev) => [...prev, line]);
          }
          setLivePlan(stablePlan);
          setLivePlanStepCompleted(stablePlanStep);
        });

        if (step.phase === 'need_tools' && step.toolCalls.length > 0) {
          iterationToolResults = await executeAgentToolCalls(step.toolCalls);
          log('[AgentFlow] orchestrate:tools_executed', { iter, count: iterationToolResults.length });
          continue;
        }

        const mapped: AgentIssue[] = step.issues.map((r) => ({
          id: nextId(),
          paragraphIndex: r.paragraphIndex,
          originalText: r.originalText,
          suggestedText: r.suggestedText,
          explanation: r.explanation || '',
          category: (r.category as AgentIssueCategory) || 'clarity',
          severity: (r.severity as AgentIssue['severity']) || 'medium',
          groupId: r.groupId,
          status: 'open',
        }));

        const roundId = nextId();
        const trimmedSummary = step.roundSummary?.trim() ?? '';
        const roundSummaryText =
          trimmedSummary ||
          (mapped.length > 0
            ? `This round produced ${mapped.length} suggestion(s) for your review.`
            : 'This round did not produce new suggestions.');

        const newRound: AgentThreadRound = {
          id: roundId,
          userMessage: newMessage,
          plan: stablePlan,
          planStepCompleted: stablePlanStep,
          activityLog: [...narrationsAccum],
          roundSummary: roundSummaryText,
          issues: mapped,
        };

        setRounds((prev) => {
          const updated = [...prev, newRound];
          setIssuesListCollapsedByRoundId(
            Object.fromEntries(updated.map((r) => [r.id, r.id !== newRound.id]))
          );
          return updated;
        });
        setExpandedCategoriesByRound((prev) => ({ ...prev, [roundId]: defaultCatExpanded() }));
        setPendingUserMessage(null);
        setLiveActivityLog([]);
        setLivePlan([]);
        setScanStatus('done');
        log('[AgentFlow] runScan:done', {
          issueCount: mapped.length,
          totalMs: Date.now() - startedAt,
          iterations: iter + 1,
        });
        return;
      }

      setScanError('Agent stopped after maximum planning steps. Try a narrower goal.');
      setScanStatus('error');
      setGoal(sentGoal);
      setPendingUserMessage(null);
    } catch (err: unknown) {
      logError('[AgentFlow] runScan:error', {
        message: err instanceof Error ? err.message : String(err),
        totalMs: Date.now() - startedAt,
      });
      setScanError('Scan failed. Please try again.');
      setScanStatus('error');
      setGoal(sentGoal);
      setPendingUserMessage(null);
    }
  }

  function updateIssueEverywhere(issueId: string, updater: (i: AgentIssue) => AgentIssue) {
    setRounds((prev) =>
      prev.map((r) => ({
        ...r,
        issues: r.issues.map((i) => (i.id === issueId ? updater(i) : i)),
      }))
    );
  }

  function ignoreIssue(id: string) {
    updateIssueEverywhere(id, (i) => ({ ...i, status: 'ignored' }));
  }

  async function replaceIssue(id: string) {
    const flat = rounds.flatMap((r) => r.issues.map((i) => ({ roundId: r.id, issue: i })));
    const found = flat.find((x) => x.issue.id === id);
    if (!found) return;
    const { issue } = found;
    const ok = await ProofreadWordAdapter.replaceText(
      issue.originalText,
      issue.suggestedText,
      issue.paragraphIndex
    );
    if (ok) {
      updateIssueEverywhere(id, (i) => ({ ...i, status: 'resolved' }));
    }
    log('[AgentFlow] replaceIssue', { id, ok });
  }

  async function replaceAllInCategory(roundId: string, cat: AgentIssueCategory) {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;
    const open = round.issues.filter((i) => i.category === cat && i.status === 'open');
    const result = await ProofreadWordAdapter.replaceAll(
      open.map((i) => ({
        originalText: i.originalText,
        suggestion: i.suggestedText,
        paragraphIndex: i.paragraphIndex,
      }))
    );
    setReplaceAllResult({ roundId, ...result });
    if (result.succeeded > 0) {
      setRounds((prev) =>
        prev.map((r) =>
          r.id !== roundId
            ? r
            : {
                ...r,
                issues: r.issues.map((i) =>
                  i.category === cat && i.status === 'open' ? { ...i, status: 'resolved' } : i
                ),
              }
        )
      );
    }
    log('[AgentFlow] replaceAllInCategory', { cat, roundId, ...result });
  }

  async function replaceAllIssuesInRound(roundId: string) {
    const round = rounds.find((r) => r.id === roundId);
    if (!round) return;
    const open = round.issues.filter((i) => i.status === 'open');
    const result = await ProofreadWordAdapter.replaceAll(
      open.map((i) => ({
        originalText: i.originalText,
        suggestion: i.suggestedText,
        paragraphIndex: i.paragraphIndex,
      }))
    );
    setReplaceAllResult({ roundId, ...result });
    if (result.succeeded > 0) {
      setRounds((prev) =>
        prev.map((r) =>
          r.id !== roundId
            ? r
            : {
                ...r,
                issues: r.issues.map((i) => (i.status === 'open' ? { ...i, status: 'resolved' } : i)),
              }
        )
      );
    }
    log('[AgentFlow] replaceAllIssuesInRound', { roundId, ...result });
  }

  function copyToClipboard(text: string) {
    try {
      navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  }

  function visibleIssues(
    cat: AgentIssueCategory,
    list: AgentIssue[],
    roundId: string
  ): AgentIssue[] {
    const fm = getFilterMode(roundId);
    const filtered = list.filter((i) => i.category === cat && (fm === 'all' || i.status === 'open'));
    return [...filtered].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }

  const openByCategory = (cat: AgentIssueCategory, list: AgentIssue[]) =>
    list.filter((i) => i.category === cat && i.status === 'open').length;

  function renderIssueCategoriesForRound(round: AgentThreadRound, interactive: boolean) {
    const roundId = round.id;
    const list = round.issues;
    const fm = getFilterMode(roundId);
    const openCount = list.filter((i) => i.status === 'open').length;
    const totalIssues = list.length;

    return (
      <>
        <div
          className={`ag-results-header${!interactive ? ' ag-results-header--readonly' : ''}`}
        >
          {openCount > 0 && (
            <span className="ag-total-badge">
              {openCount} suggestions
              {!interactive && ' · view only'}
            </span>
          )}
          {interactive && totalIssues > 0 && (
            <button
              type="button"
              className={`ag-filter-btn ${fm === 'open' ? 'active' : ''}`}
              onClick={() => setFilterMode(roundId, fm === 'open' ? 'all' : 'open')}
            >
              {fm === 'open' ? 'Open' : 'All'} <ChevronDown size={11} />
            </button>
          )}
          {interactive && openCount > 1 && (
            <button
              type="button"
              className="ag-replace-all-btn"
              onClick={() => void replaceAllIssuesInRound(roundId)}
            >
              Apply all
            </button>
          )}
        </div>

        <div className={`ag-categories${!interactive ? ' ag-categories--readonly' : ''}`}>
          {list.length === 0 ? (
            <div className="ag-empty">No suggestions in this round.</div>
          ) : (
            CATEGORY_ORDER.map((cat) => {
              const catIssues = visibleIssues(cat, list, roundId);
              const catOpen = openByCategory(cat, list);
              if (catIssues.length === 0 && fm === 'open' && catOpen === 0) return null;
              if (list.filter((i) => i.category === cat).length === 0) return null;

              const isExpanded = getExpandedSet(roundId).has(cat);
              return (
                <div key={`${roundId}-${cat}`} className="ag-category">
                  <button
                    type="button"
                    className="ag-cat-header"
                    onClick={() => toggleCategoryRound(roundId, cat)}
                    aria-expanded={isExpanded}
                  >
                    <span className={`ag-cat-icon ag-icon-${cat}`}>{CATEGORY_ICONS[cat]}</span>
                    <span className="ag-cat-title">{CATEGORY_LABELS[cat]}</span>
                    {catOpen > 0 && <span className={`ag-cat-count ag-count-${cat}`}>{catOpen}</span>}
                    {interactive && catOpen > 1 && isExpanded && (
                      <button
                        type="button"
                        className="ag-cat-apply-btn"
                        title={`Apply all ${CATEGORY_LABELS[cat]} suggestions`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void replaceAllInCategory(roundId, cat);
                        }}
                      >
                        Apply all
                      </button>
                    )}
                    <span className="ag-cat-caret">
                      {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </span>
                  </button>

                  {isExpanded && catIssues.length > 0 && (
                    <div className="ag-cat-body">
                      {catIssues.map((issue) => (
                        <div
                          key={issue.id}
                          className={`ag-issue${issue.status !== 'open' ? ' ag-issue-resolved' : ''} ag-sev-${issue.severity}`}
                        >
                          <div className="ag-issue-meta">
                            <span className={`ag-sev-dot ag-sev-dot-${issue.severity}`} />
                            <span className="ag-issue-explanation" dir="auto">
                              {issue.explanation}
                            </span>
                          </div>
                          <div
                            className="ag-suggested-preview"
                            dir={detectTextDirection(
                              `${issue.originalText}\n${issue.suggestedText}`
                            )}
                          >
                            <div
                              className="selected-text-xml"
                              // Editor mode: single inline flow with Word-style track-change colors (blue new redlines)
                              dangerouslySetInnerHTML={{
                                __html: ProofreadWordAdapter.buildTrackChangesHtml(
                                  issue.originalText,
                                  issue.suggestedText
                                ),
                              }}
                            />
                          </div>
                          {issue.status === 'open' && interactive && (
                            <div className="ag-actions">
                              <button
                                type="button"
                                className="ag-apply-btn"
                                title={`Apply: ${issue.suggestedText}`}
                                onClick={() => void replaceIssue(issue.id)}
                              >
                                Apply
                              </button>
                              <button
                                type="button"
                                className="ag-icon-btn"
                                title="Navigate to paragraph"
                                onClick={() =>
                                  ProofreadWordAdapter.navigateToParagraph(issue.paragraphIndex)
                                }
                              >
                                <Eye size={12} />
                              </button>
                              <button
                                type="button"
                                className="ag-icon-btn"
                                title="Copy suggestion"
                                onClick={() => copyToClipboard(issue.suggestedText)}
                              >
                                <Copy size={12} />
                              </button>
                              <button
                                type="button"
                                className="ag-icon-btn ag-ignore-btn"
                                title="Ignore"
                                onClick={() => ignoreIssue(issue.id)}
                              >
                                <Minus size={12} />
                              </button>
                            </div>
                          )}
                          {issue.status === 'open' && !interactive && (
                            <div className="ag-actions ag-actions--locked" aria-hidden>
                              <span className="ag-locked-hint">Locked during new run</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {isExpanded && catIssues.length === 0 && fm === 'open' && (
                    <div className="ag-cat-body">
                      <div className="ag-empty-cat">All suggestions applied or ignored</div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {interactive && replaceAllResult && replaceAllResult.roundId === roundId && (
          <div className="ag-replace-summary">
            {replaceAllResult.succeeded} applied
            {replaceAllResult.failed > 0 && `, ${replaceAllResult.failed} failed`}
          </div>
        )}
      </>
    );
  }

  const transcript = hasSubmittedOnce && (
    <div className="ag-scroll" ref={transcriptScrollRef}>
      {rounds.map((round) => {
        const latestId = rounds[rounds.length - 1]?.id;
        /** Only the current (non-stale) tail round gets full interactive suggestions */
        const isInteractiveLatest =
          round.id === latestId && !staleRoundIds.has(round.id);
        const prevIssuesCollapsed =
          !isInteractiveLatest && issuesListCollapsedByRoundId[round.id] !== false;
        const openIssuesCount = round.issues.filter((i) => i.status === 'open').length;

        const isStaleRound = staleRoundIds.has(round.id);

        return (
          <article
            key={round.id}
            className={`ag-thread-round${isStaleRound ? ' ag-thread-round--stale' : ''}`}
          >
            {isStaleRound && (
              <div className="ag-stale-banner" role="status">
                Earlier pass — kept for reference; the agent uses this when revising.
              </div>
            )}
            <div className="ag-user-bubble" dir="auto">
              {round.userMessage}
            </div>

            {round.plan.length > 0 && (
              <div className="ag-plan-card ag-plan-card--compact">
                <div className="ag-plan-title">
                  <ListOrdered size={14} aria-hidden />
                  Plan
                </div>
                <ol className="ag-plan-list">
                  {round.plan.map((step, idx) => (
                    <li
                      key={`${round.id}-p-${idx}`}
                      className={idx <= round.planStepCompleted ? 'ag-plan-done' : ''}
                    >
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {round.activityLog.length > 0 && (
              <div className="ag-activity-log ag-activity-log--done">
                {round.activityLog.map((line, i) => (
                  <div key={`${round.id}-act-${i}`} className="ag-activity-line" dir="auto">
                    {line}
                  </div>
                ))}
              </div>
            )}

            {round.roundSummary && (
              <div className="ag-round-summary-md">
                <AskMarkdown
                  content={round.roundSummary}
                  dir={detectTextDirection(round.roundSummary)}
                />
              </div>
            )}

            {isInteractiveLatest ? (
              renderIssueCategoriesForRound(round, true)
            ) : (
              <div className={`ag-prev-issues${isStaleRound ? ' ag-prev-issues--stale' : ''}`}>
                <button
                  type="button"
                  className="ag-prev-issues-toggle"
                  onClick={() => togglePrevIssuesList(round.id)}
                  aria-expanded={!prevIssuesCollapsed}
                >
                  <span className="ag-prev-issues-label">
                    {isStaleRound ? 'Suggestions (frozen)' : 'Earlier suggestions'}
                  </span>
                  <span className="ag-prev-issues-meta" dir="auto">
                    {round.issues.length === 0
                      ? 'No items'
                      : `${openIssuesCount} open · ${round.issues.length} total`}
                  </span>
                  <span className="ag-prev-issues-caret">
                    {prevIssuesCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </span>
                </button>
                {!prevIssuesCollapsed && renderIssueCategoriesForRound(round, false)}
              </div>
            )}
          </article>
        );
      })}

      {scanStatus === 'scanning' && pendingUserMessage && (
        <article className="ag-thread-round ag-thread-round--pending">
          <div className="ag-user-bubble" dir="auto">
            {pendingUserMessage}
          </div>

          {livePlan.length > 0 && (
            <div className="ag-plan-card">
              <div className="ag-plan-title">
                <ListOrdered size={14} aria-hidden />
                Plan
              </div>
              <ol className="ag-plan-list">
                {livePlan.map((step, idx) => (
                  <li
                    key={`live-${step}-${idx}`}
                    className={idx <= livePlanStepCompleted ? 'ag-plan-done' : ''}
                  >
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="ag-activity-log">
            {liveActivityLog.slice(0, -1).map((line, i) => (
              <div key={`log-${i}`} className="ag-activity-line" dir="auto">
                {line}
              </div>
            ))}
            {liveActivityLog.length > 0 && (
              <div className="ag-activity-line ag-activity-line--live" dir="auto">
                {typedNarration}
                <span className="ag-cursor">▍</span>
              </div>
            )}
            <div ref={activityEndRef} />
          </div>

          <div className="ag-scanning-inline">
            <RefreshCw size={12} className="spinning" />
            <span>Running agent…</span>
          </div>
        </article>
      )}

      {scanStatus === 'error' && scanError && (
        <div className="ag-error">
          {scanError}
          <button type="button" className="ag-retry-btn" onClick={() => void runScan()}>
            Retry
          </button>
        </div>
      )}
    </div>
  );

  const composerLabel = (
    <div className="ag-composer-label">
      <Bot size={14} aria-hidden />
      <span>
        {agentMode === 'draft'
          ? 'What should the agent draft in your document?'
          : 'What do you want to improve?'}
      </span>
    </div>
  );

  const composer = (
    <ChatComposer
      value={goal}
      onChange={setGoal}
      onSubmit={(p) => void runScan(p)}
      disabled={scanStatus === 'scanning'}
      submitting={scanStatus === 'scanning'}
      placeholder={
        hasSubmittedOnce
          ? agentMode === 'draft'
            ? 'Follow-up: e.g. add a termination clause'
            : 'Add a follow-up instruction for the agent'
          : agentMode === 'draft'
            ? 'e.g. "Draft a residential lease template"'
            : 'e.g. "Reduce legal risk"'
      }
      layout={hasSubmittedOnce ? 'sticky' : 'initial'}
      label={!hasSubmittedOnce ? composerLabel : undefined}
      followUp={hasSubmittedOnce}
      submitTitle="Run agent with tools and updated analysis"
      agentMode={agentMode}
      onAgentModeChange={setAgentMode}
      onPlaybookIconClick={() => setPlaybookPickerOpen(true)}
      selectedPlaybookLabel={
        linkedPlaybook
          ? `Playbook: ${linkedPlaybook.projectLabel} (${linkedPlaybook.recordCount} records)`
          : undefined
      }
      onClearPlaybook={() => setLinkedPlaybook(null)}
    />
  );

  return (
    <>
      <div className={`agent-view${hasSubmittedOnce ? ' agent-view--thread' : ''}`}>
        {!hasSubmittedOnce ? (
          <>
            {composer}
            <div className="ag-idle">
              <Bot size={32} className="ag-idle-icon" />
              <p>
                Choose <strong>Suggestions</strong> to review the document and get an issues list, or{' '}
                <strong>Draft</strong> to generate or insert structured text. Each reply stays in the
                thread: plans, activity, summaries, and suggestions stack in order.
              </p>
              <p className="ag-idle-hint">
                You stay in full control — every suggestion requires your approval.
              </p>
            </div>
          </>
        ) : (
          <>
            {transcript}
            {composer}
          </>
        )}
      </div>

      {playbookPickerOpen && (
        <PlaybookPickerModal
          preferredProjectId={parentProjectId}
          workflowHint={workflowHint}
          onSelect={(sel) => setLinkedPlaybook(sel)}
          onClose={() => setPlaybookPickerOpen(false)}
        />
      )}
    </>
  );
};
